<template>
  <div class="widget-preview-panel">
    <v-card class="preview-card" flat>
      <v-card-title class="d-flex align-center justify-space-between gap-3 mt-2 py-1">
        <div class="d-flex align-center">
          <v-icon color="primary" aria-hidden="true" left>mdi-eye</v-icon>
          <h2 class="text-h6 font-weight-medium mr-3">Preview</h2>
          <widget-editor-test-indicator />
        </div>

        <div class="d-flex align-center gap-2">
          <v-btn-toggle
            v-model="size_mode"
            mandatory
            dense
            class="preview-size-toggle"
            @change="$emit('size-mode-change', $event)"
          >
            <v-btn x-small :value="0">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-icon small left>mdi-fit-to-screen-outline</v-icon>
                    Fit
                  </span>
                </template>
                <span>Scale to fit available space</span>
              </v-tooltip>
            </v-btn>
            <v-btn x-small :value="1">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-icon small left>mdi-ruler</v-icon>
                    Actual
                  </span>
                </template>
                <span>Show at actual device size</span>
              </v-tooltip>
            </v-btn>
          </v-btn-toggle>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon outlined @click="toggleFullscreen" v-bind="attrs" v-on="on">
                <v-icon>{{ is_fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ is_fullscreen ? 'Exit Full Screen' : 'Full Screen' }}</span>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text class="preview-card-body">
        <div class="preview-container" :class="preview_container_classes" ref="preview-container">
          <slot name="floating-controls" />

          <v-overlay absolute :value="is_loading" class="preview-overlay">
            <v-progress-circular indeterminate color="primary" />
          </v-overlay>

          <div class="iframe-wrapper" :class="iframe_wrapper_classes" :style="iframe_wrapper_style">
            <slot name="preview" />
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import widgetEditorTestIndicator from './widget-editor-test-indicator.vue';

/**
 * Reusable preview panel for widget edit screens (right pane).
 * Handles fit/actual size modes, fullscreen toggle, device frames, and scaling.
 *
 * @example
 * <widget-preview-panel
 *   :preview_mode="preview_mode"
 *   :is_fullscreen="settings_collapsed"
 *   :is_loading="loading"
 *   @fullscreen-toggle="settings_collapsed = !settings_collapsed"
 * >
 *   <template #floating-controls>
 *     <widget-editor-floating-preview-control ... />
 *   </template>
 *   <template #preview>
 *     <iframe ... />
 *   </template>
 * </widget-preview-panel>
 */
export default {
  name: 'WidgetPreviewPanel',

  components: {
    widgetEditorTestIndicator,
  },

  props: {
    /**
     * Preview mode: 'mobile', 'tablet', 'tablet-landscape', 'desktop'
     */
    preview_mode: {
      type: String,
      default: 'tablet-landscape',
      validator: value => ['mobile', 'tablet', 'tablet-landscape', 'desktop'].includes(value),
    },

    /**
     * Whether preview is in fullscreen mode
     */
    is_fullscreen: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether preview is loading
     */
    is_loading: {
      type: Boolean,
      default: false,
    },

    /**
     * Preview dimensions for current mode
     */
    dimensions: {
      type: Object,
      default: () => ({
        width: 1366,
        height: 1024,
      }),
    },

    /**
     * Initial size mode (0 = fit, 1 = actual)
     */
    initial_size_mode: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      size_mode: this.initial_size_mode,
      calculated_scale: 1,
    };
  },

  computed: {
    preview_container_classes() {
      return {
        'mobile-preview': this.preview_mode === 'mobile',
        'tablet-preview': this.preview_mode === 'tablet',
        'tablet-landscape-preview': this.preview_mode === 'tablet-landscape',
        'fit-mode': this.size_mode === 0,
      };
    },

    iframe_wrapper_classes() {
      return {
        'mobile-frame': this.preview_mode === 'mobile',
        'tablet-frame': this.preview_mode === 'tablet',
        'tablet-landscape-frame': this.preview_mode === 'tablet-landscape',
      };
    },

    iframe_wrapper_style() {
      const scale = this.size_mode === 1 ? 1 : this.calculated_scale;
      return {
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        width: `${this.dimensions.width}px`,
        height: `${this.dimensions.height}px`,
      };
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.calculateScale();
    });
    window.addEventListener('resize', this.calculateScale);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateScale);
  },

  watch: {
    preview_mode() {
      this.$nextTick(() => {
        this.calculateScale();
      });
    },

    is_fullscreen() {
      this.$nextTick(() => {
        this.calculateScale();
      });
    },

    size_mode(newVal) {
      this.$nextTick(() => {
        this.calculateScale();

        // scroll to top when switching to fit mode
        if (newVal === 0) {
          const container = this.$refs['preview-container'];
          if (container) {
            this.$nextTick(() => {
              container.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth',
              });
            });
          }
        }
      });
    },

    dimensions() {
      this.$nextTick(() => {
        this.calculateScale();
      });
    },
  },

  methods: {
    toggleFullscreen() {
      this.$emit('fullscreen-toggle');
    },

    getContainerElement() {
      return this.$refs['preview-container'];
    },

    calculateScale() {
      const container = this.$refs['preview-container'];
      if (!container) {
        setTimeout(() => this.calculateScale(), 100);
        return;
      }

      const containerWidth = container.clientWidth - 40;
      const containerHeight = container.clientHeight - 40;

      if (containerWidth <= 0 || containerHeight <= 0) {
        setTimeout(() => this.calculateScale(), 100);
        return;
      }

      const scaleX = containerWidth / this.dimensions.width;
      const scaleY = containerHeight / this.dimensions.height;

      this.calculated_scale = Math.min(scaleX, scaleY, 1);
    },
  },
};
</script>

<style scoped lang="scss">
.widget-preview-panel {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.preview-card {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  width: 100%;
  height: 100%;
}

.preview-card-body {
  flex: 1 1 auto;
  display: flex;
  padding: 0;
  overflow: auto;
}

.preview-container {
  height: 100%;
  width: 100%;
  overflow: auto;
  min-height: 0;
  position: static;
}

.preview-container.fit-mode {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
}

.preview-container.mobile-preview,
.preview-container.tablet-preview,
.preview-container.tablet-landscape-preview {
  background: #ffffff;
  padding: 20px;
}

.iframe-wrapper {
  margin-left: auto;
  margin-right: auto;
  position: relative;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  overflow: visible;
}

.iframe-wrapper.mobile-frame {
  border: 8px solid #1a1a1a;
  border-radius: 40px;
  box-shadow: 0 0 0 2px #333, 0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 1px #444;
  background: #000;
  padding: 20px 12px;
}

.iframe-wrapper.tablet-frame {
  border: 4px solid #2a2a2a;
  border-radius: 20px;
  box-shadow: 0 0 0 2px #444, 0 8px 24px rgba(0, 0, 0, 0.3), inset 0 0 0 1px #555;
  background: #111;
  padding: 15px 10px;
}

.iframe-wrapper.tablet-landscape-frame {
  border: 4px solid #2a2a2a;
  border-radius: 20px;
  box-shadow: 0 0 0 2px #444, 0 8px 24px rgba(0, 0, 0, 0.3), inset 0 0 0 1px #555;
  background: #111;
  padding: 10px 15px;
}

.iframe-wrapper.mobile-frame::after {
  content: '';
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 6px;
  background: #333;
  border-radius: 3px;
  z-index: 3;
}

.preview-overlay {
  z-index: 1;
}

.preview-size-toggle {
  margin-right: 8px;
}

.preview-size-toggle .v-btn {
  font-size: 0.85rem;
  padding: 1rem;
  text-transform: none;
}
</style>
